import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Stack,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { EventData } from '../../types/event';
import { CategoryBadge } from './CategoryBadge';
import { DeadlineLabel } from './DeadlineLabel';

const DAY_OF_WEEK = ['日', '月', '火', '水', '木', '金', '土'];

const formatEventDate = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.getMonth() + 1}月${d.getDate()}日（${DAY_OF_WEEK[d.getDay()]}）`;
};

const formatTime = (t: string): string => t.slice(0, 5);

interface EventCardProps {
  event: EventData;
  onDetail: (event: EventData) => void;
}

export const EventCard: React.FC<EventCardProps> = ({ event, onDetail }) => {
  const isPast = event.is_past;
  const isOnline = event.location_type === 'online';

  return (
    <Card
      elevation={isPast ? 0 : 2}
      sx={{
        opacity:         isPast ? 0.55 : 1,
        backgroundColor: isPast ? '#F5F5F5' : '#fff',
        display:         'flex',
        flexDirection:   'column',
        height:          '100%',
        position:        'relative',
        border: event.is_participated
          ? '2px solid #1976D2'
          : event.is_own_company
            ? '2px solid #2E5BBA'
            : undefined,
      }}
    >
      {/* 参加済みアイコン（左上） */}
      {event.is_participated && (
        <Box sx={{ position: 'absolute', top: 12, left: 12, zIndex: 1 }}>
          <CheckCircleIcon sx={{ color: '#1976D2', fontSize: 20 }} />
        </Box>
      )}

      {/* 自社バッジ（右上） */}
      {event.is_own_company && !event.is_participated && (
        <Box sx={{
          position:        'absolute',
          top:             8,
          right:           8,
          zIndex:          1,
          backgroundColor: '#2E5BBA',
          color:           '#fff',
          borderRadius:    1,
          px:              0.75,
          py:              0.25,
          fontSize:        '0.65rem',
          fontWeight:      700,
          lineHeight:      1.4,
        }}>
          自社
        </Box>
      )}

      <CardContent sx={{ flex: 1, pt: 2, pb: 1 }}>
        {/* 企業アイコン + 企業名 */}
        <Stack direction="row" spacing={1} alignItems="center" mb={0.75}>
          <Box
            sx={{
              width:           32,
              height:          32,
              borderRadius:    1,
              backgroundColor: '#EEF2FF',
              display:         'flex',
              alignItems:      'center',
              justifyContent:  'center',
              flexShrink:      0,
            }}
          >
            <BusinessIcon sx={{ fontSize: 18, color: '#5C6BC0' }} />
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              overflow:        'hidden',
              display:         '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {event.company_name}
          </Typography>
        </Stack>

        {/* 種別バッジ or 終了バッジ（企業名の下・別行） */}
        <Box mb={1} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          {isPast ? (
            <Box sx={{ display: 'inline-block', backgroundColor: '#9E9E9E', color: '#fff', borderRadius: 1, px: 1, py: 0.25, fontSize: '0.7rem', fontWeight: 700 }}>
              終了
            </Box>
          ) : (
            <CategoryBadge category={event.category} />
          )}
          {(event.tags ?? []).slice(0, 2).map((tag) => (
            <Box
              key={tag.id}
              sx={{ display: 'inline-block', backgroundColor: '#ECEFF1', color: '#546E7A', borderRadius: '6px', px: 0.75, py: 0.25, fontSize: '0.65rem', fontWeight: 600 }}
            >
              {tag.name}
            </Box>
          ))}
          {(event.tags ?? []).length > 2 && (
            <Box sx={{ display: 'inline-block', backgroundColor: '#ECEFF1', color: '#546E7A', borderRadius: '6px', px: 0.75, py: 0.25, fontSize: '0.65rem', fontWeight: 600 }}>
              +{(event.tags ?? []).length - 2}
            </Box>
          )}
        </Box>

        {/* タイトル */}
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{
            overflow:        'hidden',
            display:         '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            mb:              0.75,
            lineHeight:      1.4,
          }}
        >
          {event.title}
        </Typography>

        {/* 開催日 */}
        <Stack direction="row" spacing={0.75} alignItems="center" mb={0.5}>
          <CalendarTodayIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {formatEventDate(event.event_date)}
          </Typography>
        </Stack>

        {/* 時刻 */}
        <Stack direction="row" spacing={0.75} alignItems="center" mb={0.5}>
          <AccessTimeIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {formatTime(event.start_time)} - {formatTime(event.end_time)}
          </Typography>
        </Stack>

        {/* 場所（オンライン：地球アイコン / オフライン：ピンアイコン+場所名・2行省略） */}
        <Stack direction="row" spacing={0.75} alignItems="flex-start" mb={1}>
          {isOnline ? (
            <LanguageIcon sx={{ fontSize: 13, color: 'text.secondary', mt: '1px', flexShrink: 0 }} />
          ) : (
            <LocationOnIcon sx={{ fontSize: 13, color: 'text.secondary', mt: '1px', flexShrink: 0 }} />
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              overflow:        'hidden',
              display:         '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight:      1.5,
            }}
          >
            {isOnline ? 'オンライン' : event.location}
          </Typography>
        </Stack>

        {/* 締切ラベル */}
        <DeadlineLabel event={event} />
      </CardContent>

      <CardActions sx={{ pt: 0, px: 2, pb: 1.5, justifyContent: 'flex-end' }}>
        <Button
          size="small"
          variant="contained"
          onClick={() => onDetail(event)}
        >
          詳細を見る
        </Button>
      </CardActions>
    </Card>
  );
};
